import { useCallback, useState } from "react";

interface Props {
  id: string;
  tag: string | undefined;
  colIndex: number;
  onSelectChange?: (value: string, colIndex: number, className: string) => void;
}

/**
 * Custom hook for handling Arbema select value changes.
 *
 * @param props - An object containing the following properties:
 *   - id: A string representing the unique identifier for the select.
 *   - tag: tag that sits in select obj to determine the class name.
 *   - colIndex: column index of the select component.
 *   - onSelectChange: callback function called when select value changes.
 */
const useArbemaSelectValue = ({ tag, colIndex, onSelectChange }: Props) => {
  const [selectedValue, setSelectedValue] = useState<string>("");
  const getClassNameFromTag = useCallback(
    (tag: string | undefined): string | null => {
      if (!tag) return null;

      // Handle engine tag with colIndex
      if (tag === "engine") {
        return `engine${colIndex}`;
      }

      return null;
    },
    [colIndex],
  );
  const handleSelectChange = useCallback(
    (value: string) => {
      setSelectedValue(value);
      const className = getClassNameFromTag(tag);

      if (className && onSelectChange) {
        onSelectChange(value, colIndex, className);
      }
    },
    [tag, colIndex, onSelectChange, getClassNameFromTag],
  );

  return {
    handleSelectChange,
    className: getClassNameFromTag(tag),
    selectedValue,
    colIndex,
  };
};

export default useArbemaSelectValue;
